/**
 * 2대2 같은 편 팀 채팅 — 말풍선 방식 (2026-09-22).
 *
 * ## 왜 팀 채팅만인가
 *
 * 폰에서 배틀 페이지를 열어 둔 채 디스코드로 넘어가면 **3초 만에 소켓이 죽고, 돌아와도
 * 새로고침해야 붙는다**(단면도 「모바일 백그라운드 복귀」 실측표). 즉 모바일 유저는 판 도중에
 * 디스코드로 갈 수 없다. 팀 음성방(§3.13)은 배포 뒤 76판 동안 **한 번도 안 열렸다**.
 *
 * 전체 채팅·배틀방 채팅은 만들지 않는다 (사용자 확정). 전체 채팅은 체리 거래 창구가 되고,
 * 배틀방 채팅은 관전자에게 새고 익명이 말투로 드러난다.
 *
 * ## 채팅창이 없다 — 말은 필드 안에 뜬다 (2026-09-22 사용자 결정)
 *
 * 처음엔 조작부 아래 「띠」(접힘 72px · 펼침 157px)를 뒀는데, 모바일에서 배틀 화면이 가뜩이나
 * 작은데 세로를 더 먹었다. 그래서 **이미 있는 장치를 쓴다** — 담임(체리·베리·리치)이 배틀 화면
 * 안에서 얼굴과 함께 말하는 그 말풍선이다(`patch-school-coach.py` 의 `.cherish-coach`).
 * 팀원의 말도 **그 유저의 아바타와 함께 필드 위에 뜨고 6초 뒤 사라진다.**
 * 화면에 남는 건 **입력칸 한 줄**뿐이라 세로 비용이 72 → 45px 로 줄었다.
 *
 * ⚠️ **말풍선은 640×360 좌표계 안이다.** `.innerbattle` 은 늘 640×360 이고 바깥 `.battle`(=$frame)이
 *    `transform: scale()` 로 줄인다. 그래서 여기 적는 px·pt 는 전부 **640칸 기준**이고, 화면에
 *    보이는 크기는 그 값 × 배율이다(폰 0.54 · PC 1.07). 모바일용 축소 미디어쿼리를 따로 걸면
 *    두 번 줄어들어 글씨가 뭉갠다 — 담임 말풍선이 지금 그 상태다(고치지 않았다, 그쪽 소유).
 *
 * ⚠️ 못 본 말은 그냥 사라진다. 대신 **쿨타임을 표시 시간과 같게** 둬서, 안 읽힌 말이 다음 말에
 *    덮이지 않는다. 회색 이전 줄이 한 겹 더 받쳐 준다. (사용자: 「유저가 배틀화면 말고 다른곳에
 *    집중할리없잖아」 — 놓침 대비는 더 하지 않는다.)
 *
 * ## 통로
 *
 * 이미 깔려 있는 쇼다운 웹소켓을 봉투로만 쓴다 — **새 통로·폴링·SSE 가 0이다**. 보내는 건
 * `/cherishteamchat <말>`, 받는 건 `|cherishteamchat|<json>`(같은 편에게만 sendTo).
 *
 * 정본은 cherishpro 레포 scripts/showdown-fork/cherish-team-chat.tsx.
 */
import preact from "../js/lib/preact";
import { PS } from "./client-main";
import { Dex } from "./battle-dex";
import type { BattleRoom } from "./panel-battle";

/** 한 번에 보낼 수 있는 길이 (2026-09-22 사용자 결정 — 미리보기로 30자 채택). */
export const CWTC_MAX_LEN = 30;
/** 말풍선이 떠 있는 시간. */
const CWTC_SHOW_MS = 6000;
/** 같은 사람이 다음 말을 보낼 수 있을 때까지. **표시 시간과 같게 둔다** — 안 읽힌 말을 덮지 않으려고. */
const CWTC_COOLDOWN_MS = 6000;

interface CwtcRoomState {
	/** 서버가 「이 방에서 팀 채팅을 열어도 된다」고 답했는가. 이 값이 참일 때만 입력칸을 그린다. */
	ready: boolean;
	/** 쓰다 만 글자. 매 턴 재렌더·리마운트에도 살아남아야 하는 값이라 화면 밖에 둔다. */
	draft: string;
	/** 마지막으로 보낸 시각 (쿨타임 계산). */
	sentAt: number;
	/** 못 보낸 이유 — 입력칸에 잠깐 띄운다. 필드를 덮지 않는다. */
	notice: string;
	noticeUntil: number;
	askedAt: number;
	asks: number;
}

/** 화면 밖에 사는 방별 상태. 조각이 다시 마운트돼도 여기 남는다. */
const cwtcRooms = new Map<string, CwtcRoomState>();

export function cwtcState(roomid: string): CwtcRoomState {
	let st = cwtcRooms.get(roomid);
	if (!st) {
		st = { ready: false, draft: '', sentAt: 0, notice: '', noticeUntil: 0, askedAt: 0, asks: 0 };
		cwtcRooms.set(roomid, st);
	}
	return st;
}

/* ── 말풍선 ──────────────────────────────────────────────────────────────── */

/**
 * 그 사람의 아바타 그림.
 *
 * ⚠️ `#cherish-N` 은 **`-full.png` 만 확실히 있다**(patch-battle-view.py 가 트레이너 칸에 쓰는
 *    바로 그 파일이다). 전신 그림이라 동그란 칸에는 위쪽(얼굴)을 잘라 쓴다 — CSS `object-position`.
 *    익명은 `#cherish-0`(실루엣)로 굳어 있어 여기서 따로 가릴 것이 없다.
 */
function cwtcAvatarUrl(avatar: string): string {
	const a = String(avatar || '');
	if (a.startsWith('#cherish-')) return `/sprites/trainers-custom/cherish${a.slice(9)}-full.png`;
	try {
		return Dex.resolveAvatar(a || 'unknown');
	} catch {
		return '';
	}
}

/** 이 방에서 그 userid 가 앉은 자리의 아바타. 못 찾으면 빈 문자열(이모지 폴백). */
function cwtcAvatarOf(room: BattleRoom, userid: string): string {
	const sides: any[] = (room.battle as any)?.sides || [];
	for (const side of sides) {
		if (side && String(side.id) === userid) return cwtcAvatarUrl(side.avatar);
	}
	return '';
}

/** 말풍선이 들어갈 자리 — 640×360 좌표계(`.innerbattle`). 아직 없으면 null. */
function cwtcFieldEl(room: BattleRoom): HTMLElement | null {
	const $b: any = (room.battle as any)?.scene?.$battle;
	return $b && $b.length ? ($b[0] as HTMLElement) : null;
}

const cwtcHideTimers = new Map<string, number>();

/**
 * 한 줄을 필드 위에 띄운다 (담임 말풍선과 같은 뼈대, 다른 요소).
 *
 * ⚠️ **담임과 한 요소를 쓰면 안 된다.** 담임이 말하는 순간 팀원 말이 지워진다. 자리도 겹치므로
 *    CSS 가 팀원 말풍선이 떠 있는 동안 담임을 한 층 위로 올린다.
 */
function cwtcShow(room: BattleRoom, name: string, text: string, userid: string) {
	const field = cwtcFieldEl(room);
	if (!field) {
		// 필드 DOM 이 아직 없다(입장 직후). 몇 번만 다시 시도한다 — 담임 말풍선과 같은 방식.
		const st: any = room as any;
		st.cwtcTries = (st.cwtcTries || 0) + 1;
		if (st.cwtcTries < 20) setTimeout(() => cwtcShow(room, name, text, userid), 250); // poll-ok: 요청이 아니라 DOM 대기
		return;
	}
	(room as any).cwtcTries = 0;

	let el = field.querySelector('.cw-tsay') as HTMLElement | null;
	if (!el) {
		el = document.createElement('div');
		el.className = 'cw-tsay';
		el.innerHTML =
			'<div class="cw-tsay-face"><img alt=""><span>🙂</span></div>' +
			'<div class="cw-tsay-body"><div class="cw-tsay-name"></div>' +
			'<div class="cw-tsay-prev"></div><div class="cw-tsay-text"></div></div>';
		field.appendChild(el);
	}

	const img = el.querySelector('.cw-tsay-face img') as HTMLImageElement;
	const url = cwtcAvatarOf(room, userid);
	if (url && img.getAttribute('src') !== url) {
		img.setAttribute('src', url);
		img.style.display = '';
		img.onerror = () => { img.style.display = 'none'; };
	}
	// ⚠️ 이름·본문은 반드시 textContent 로 넣는다 (innerHTML 금지 — 남이 정한 문자열이다).
	(el.querySelector('.cw-tsay-name') as HTMLElement).textContent = name;
	const textEl = el.querySelector('.cw-tsay-text') as HTMLElement;
	(el.querySelector('.cw-tsay-prev') as HTMLElement).textContent = textEl.textContent || '';
	textEl.textContent = text;
	el.classList.toggle('cw-tsay-me', userid === PS.user.userid);

	el.classList.remove('cw-tsay-hide', 'cw-tsay-pop');
	void el.offsetWidth; // 애니메이션 재시작
	el.classList.add('cw-tsay-pop');
	/*
	 * ⚠️ 담임 말풍선을 우리 위로 밀어 올릴 **실제 높이**를 알려 준다.
	 *    고정값(한 줄 기준)으로 밀면 두 줄짜리 말에서 그대로 겹친다 — 09-22 미리보기에서 실제로 겹쳤다.
	 *    offsetHeight 는 transform 앞의 값이라 640칸 기준으로 그대로 쓸 수 있다.
	 */
	field.style.setProperty('--cw-tsay-h', `${el.offsetHeight}px`);

	const old = cwtcHideTimers.get(room.id);
	if (old) clearTimeout(old);
	cwtcHideTimers.set(room.id, window.setTimeout(() => {
		el!.classList.add('cw-tsay-hide');
	}, CWTC_SHOW_MS) as unknown as number);
}

/**
 * 서버가 밀어 준 것 — panel-battle 의 `case 'cherishteamchat'` 이 부른다.
 *
 * 세 모양뿐이다:
 *   - `{ ready: 1, history: [...] }` — 이 방에서 열어도 된다 (+ 아주 최근 줄이 있으면 그것만)
 *   - `{ line: { userid, name, text, at } }` — 한 줄
 *   - `{ notice: '...' }`                    — 내 화면에만 뜨는 안내 (입력칸에 잠깐)
 */
export function cherishTeamChatReceive(room: BattleRoom, data: any): void {
	const st = cwtcState(room.id);
	if (data?.ready) {
		st.ready = true;
		/*
		 * 이력은 **되살리지 않는다**. 말풍선은 6초짜리라 옛 줄을 되띄우면 방금 온 말처럼 보인다.
		 * 새로고침 직전(6초 안)에 온 줄만 이어서 보여 준다 — 그 경우엔 아직 살아 있던 말이다.
		 */
		const list: any[] = Array.isArray(data.history) ? data.history : [];
		const last = list[list.length - 1];
		if (last && Date.now() - Number(last.at || 0) < CWTC_SHOW_MS) {
			cwtcShow(room, String(last.name || ''), String(last.text || ''), String(last.userid || ''));
		}
	}
	if (data?.line) {
		st.ready = true;
		cwtcShow(room, String(data.line.name || ''), String(data.line.text || ''), String(data.line.userid || ''));
	}
	if (data?.notice) {
		st.notice = String(data.notice);
		st.noticeUntil = Date.now() + 4000;
	}
}

/** 이 방에서 띠를 물어볼 자리인가 — 2대2(`multi`) 이고 내가 참가자일 때만. */
function cwtcEligible(room: BattleRoom): boolean {
	return room.battle?.gameType === 'multi' && !!room.side;
}

/**
 * 같은 편에게 보내는 입력칸 한 줄.
 *
 * ⚠️ **봇이 팀원이어도 분기 없이 똑같이 연다** (사용자 결정). `if (팀원이 봇) 숨김` 같은 줄은
 *    조건이 바뀌는 날 유령 버그가 된다. 빈 채팅 칸은 해롭지 않다.
 */
export class CherishTeamChat extends preact.Component<{ room: BattleRoom, width: number }> {
	input: HTMLInputElement | null = null;
	/** 쿨타임이 도는 동안 1초마다 다시 그린다. */
	tick: number | null = null;
	/** 키패드가 올라온 높이. 0 이면 내려가 있다. */
	lift = 0;

	override componentDidMount() {
		this.ask();
	}

	override componentDidUpdate() {
		this.ask();
	}

	override componentWillUnmount() {
		this.unwatchKeyboard();
		if (this.tick) { clearInterval(this.tick); this.tick = null; }
	}

	/**
	 * 「여기서 팀 채팅을 열어도 되나」를 서버에 묻는다.
	 *
	 * ⚠️ 마운트 한 번으로는 못 묻는다 — 그 시점엔 `gameType` 도 `side` 도 아직 안 와 있다.
	 *    그래서 **자격이 갖춰진 첫 렌더**에 묻고, 답이 없으면 몇 번만 더 묻는다. 답을 받으면
	 *    `ready` 가 서고 이 함수는 그대로 멈춘다 — 주기적으로 도는 타이머가 아니다.
	 */
	ask() {
		const room = this.props.room;
		const st = cwtcState(room.id);
		if (st.ready || !cwtcEligible(room)) return;
		const now = Date.now();
		if (st.asks >= 5 || now - st.askedAt < 4000) return;
		st.askedAt = now;
		st.asks++;
		// sendDirect — 클라의 명령 파서를 안 태운다 (그쪽이 `/` 를 가로챈다).
		room.sendDirect('/cherishteamchat');
	}

	/* ── 모바일 키패드 ──────────────────────────────────────────────────────
	 * 키패드는 레이아웃 뷰포트를 줄이지 않으므로 그냥 두면 입력칸이 키패드 밑에 깔린다.
	 * 포커스가 가 있는 동안만 입력칸을 키패드 위로 띄운다.
	 * (자동 포커스는 `patch-mobile-no-autofocus.py` 가 이미 막아 뒀다 — 스스로 뜨지는 않는다.)
	 */
	viewport(): any {
		return typeof window !== 'undefined' ? (window as any).visualViewport : null;
	}

	onViewport = () => {
		const vv = this.viewport();
		if (!vv) return;
		const gap = Math.round(window.innerHeight - (vv.height + (vv.offsetTop || 0)));
		// 80px 아래는 주소창 같은 잔움직임이다 — 키패드로 안 친다.
		const lift = gap > 80 ? gap : 0;
		if (lift === this.lift) return;
		this.lift = lift;
		this.forceUpdate();
	};

	watchKeyboard() {
		const vv = this.viewport();
		if (!vv) return;
		vv.addEventListener('resize', this.onViewport);
		vv.addEventListener('scroll', this.onViewport);
		this.onViewport();
	}

	unwatchKeyboard() {
		const vv = this.viewport();
		if (vv) {
			vv.removeEventListener('resize', this.onViewport);
			vv.removeEventListener('scroll', this.onViewport);
		}
		if (this.lift) {
			this.lift = 0;
			this.forceUpdate();
		}
	}

	onFocus = () => { this.watchKeyboard(); };
	onBlur = () => { this.unwatchKeyboard(); };

	/* ── 보내기 ─────────────────────────────────────────────────────────── */

	cooldownLeft(): number {
		const st = cwtcState(this.props.room.id);
		return Math.max(0, Math.ceil((st.sentAt + CWTC_COOLDOWN_MS - Date.now()) / 1000));
	}

	/** 쿨타임·안내가 도는 동안만 1초 시계를 켠다. 끝나면 스스로 끈다. */
	syncTick() {
		const st = cwtcState(this.props.room.id);
		const busy = this.cooldownLeft() > 0 || st.noticeUntil > Date.now();
		if (busy && !this.tick) {
			this.tick = window.setInterval(() => this.forceUpdate(), 1000) as unknown as number; // poll-ok: 남은 초 표시
		} else if (!busy && this.tick) {
			clearInterval(this.tick);
			this.tick = null;
		}
	}

	send = () => {
		const room = this.props.room;
		const st = cwtcState(room.id);
		if (this.cooldownLeft() > 0) return;
		const text = st.draft.trim().slice(0, CWTC_MAX_LEN);
		if (!text) return;
		/*
		 * ⚠️ `/` 로 시작하는 말은 **애초에 서버로 안 보낸다.** 보내면 명령 화이트리스트가
		 *    거부 문구(영어 원문)를 유저 화면에 띄운다 — 단면도 §4-7 과 같은 이유다.
		 */
		if (text.startsWith('/')) {
			st.notice = '「/」로 시작하는 말은 보낼 수 없어요.';
			st.noticeUntil = Date.now() + 4000;
			this.forceUpdate();
			return;
		}
		room.sendDirect(`/cherishteamchat ${text}`);
		st.draft = '';
		st.sentAt = Date.now();
		if (this.input) this.input.value = '';
		this.forceUpdate();
	};

	onInput = (e: Event) => {
		const el = e.currentTarget as HTMLInputElement;
		cwtcState(this.props.room.id).draft = el.value;
	};

	onKeyDown = (e: KeyboardEvent) => {
		// 배틀 화면의 단축키로 새어 나가지 않게 한다.
		e.stopPropagation();
		if (e.key === 'Enter') {
			e.preventDefault();
			this.send();
		}
	};

	/** 패널 래퍼의 focusClick 이 우리 입력칸에서 포커스를 뺏지 않게 막는다. */
	stop = (e: Event) => { e.stopPropagation(); };

	/**
	 * ⚠️ 버튼을 눌러도 입력칸의 포커스를 빼앗지 않는다.
	 *
	 * 안 막으면 모바일에서 「보내기」가 안 먹는다: 누르는 순간 blur → 키패드가 내려가며 칸이
	 * 제자리로 돌아가고, 버튼이 손가락 밑에서 사라진 뒤에 click 이 온다.
	 */
	keepFocus = (e: Event) => { e.preventDefault(); };

	override render() {
		const room = this.props.room;
		const st = cwtcState(room.id);
		if (!st.ready) return null;
		this.syncTick();

		const left = this.cooldownLeft();
		const notice = st.noticeUntil > Date.now() ? st.notice : '';
		const style: any = { width: `${this.props.width}px` };
		if (this.lift) {
			// 키패드 위로. 입력칸 한 줄뿐이라 떠올라도 가리는 것이 거의 없다.
			style.position = 'fixed';
			style.left = '8px';
			style.right = '8px';
			style.bottom = `${this.lift + 6}px`;
			style.width = 'auto';
			style.zIndex = '80';
		}

		return <div class={`cw-tchat${notice ? ' cw-tchat-notice' : ''}`} style={style} onClick={this.stop}>
			<input
				class="cw-tchat-input"
				type="text"
				maxLength={CWTC_MAX_LEN}
				placeholder={notice || `같은 편에게 (${CWTC_MAX_LEN}자)`}
				aria-label="같은 편에게 보낼 말"
				value={st.draft}
				ref={el => { this.input = el as HTMLInputElement; }}
				onInput={this.onInput}
				onKeyDown={this.onKeyDown}
				onFocus={this.onFocus}
				onBlur={this.onBlur}
			/>
			<button
				class="cw-tchat-send"
				disabled={left > 0}
				onMouseDown={this.keepFocus}
				onClick={this.send}
			>{left > 0 ? `${left}` : '보내기'}</button>
		</div>;
	}
}
